import { useUser } from "@/backend";
import { useOrders } from "@/backend/orders/infrastructura/useOrders";
import { messagePromise } from "@/utils/messagePromise";
import {
  addToast,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import React from "react";
import { OrderCard } from "./components/OrderCard";
import { useShopper } from "@/backend/store/infrastructure/useStore";
import { ShopperStatus } from "@/backend/store/domain/Shopper";
import Link from "next/link";

function Home() {
  const { user, shopper, isAdmin } = useUser();
  const { orders, updateOrder } = useOrders();

  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const [modalAction, setModalAction] = React.useState<
    "activate" | "deactivate"
  >("deactivate");

  const { updateShopper } = useShopper();
  const isAccountActive = shopper?.status === ShopperStatus.ACTIVE;

  const handleAccountToggle = () => {
    setModalAction(isAccountActive ? "deactivate" : "activate");
    onOpen();
  };

  const confirmAccountAction = () => {
    addToast({
      title: "Actualizando cuenta",
      promise: updateShopper.mutateAsync({
        id: shopper?.id ?? "",
        shopper: {
          status:
            modalAction === "activate"
              ? ShopperStatus.ACTIVE
              : ShopperStatus.INACTIVE,
        },
      }),
    });

    onOpenChange();
  };

  const handleCompletePickupOrder = async (orderId: string) => {
    try {
      await messagePromise(
        updateOrder({
          id: orderId,
          order: {
            status: "COMPLETED",
          },
        }),
        {
          pending: "Completando orden...",
          success: "Orden completada exitosamente",
          error: "Error al completar la orden",
        }
      );
    } catch (error) {
      console.error("Error al completar la orden:", error);
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 lg:px-8 py-6 max-w-7xl">
        {/* Header simple */}
        <div className="mb-8 pb-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {/* @ts-ignore */}
                Bienvenido {user?.name}
              </h1>
              <p className="text-gray-600 flex items-center gap-2">
                <Icon icon="lucide:store" className="text-lg text-primary" />
                {shopper?.store.name}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
                <div
                  className={`w-2 h-2 rounded-full ${isAccountActive ? "bg-green-500" : "bg-red-500"}`}
                />
                <span
                  className={`text-sm font-medium ${isAccountActive ? "text-green-700" : "text-red-700"}`}
                >
                  {isAccountActive ? "Activa" : "Inactiva"}
                </span>
              </div>

              <Button
                isIconOnly
                color={isAccountActive ? "danger" : "success"}
                variant="flat"
                onPress={handleAccountToggle}
                title={isAccountActive ? "Desactivar cuenta" : "Activar cuenta"}
              >
                <Icon
                  icon={isAccountActive ? "lucide:power-off" : "lucide:power"}
                  className="text-xl"
                />
              </Button>
            </div>
          </div>
        </div>
        {orders?.filter((x) => x.status == "ACCEPTED_STORE")?.length == 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Icon
              icon="lucide:inbox"
              className="text-5xl text-gray-400 mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No hay pedidos pendientes
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Aún no tienes pedidos asignados. Aparecerán aquí cuando estén
              listos para procesarlos.
            </p>
            <Button
              as={Link}
              href="/report"
              color="primary"
              variant="flat"
              startContent={<Icon icon="lucide:bar-chart-3" />}
            >
              Ver historial
            </Button>
          </div>
        ) : (
          <div className="space-y-6 lg:space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200/50 p-6 lg:p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:list" className="text-3xl text-primary" />
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Órdenes Activas
                  </h2>
                </div>
                <span className="bg-primary/10 text-primary text-sm font-semibold px-3 py-1 rounded-full">
                  {orders.filter((x) => x.status == "ACCEPTED_STORE").length}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                {orders
                  .filter((x) => x.status == "ACCEPTED_STORE")
                  ?.map((order) => <OrderCard key={order.id} order={order} />)}
              </div>
            </div>

            {/* Sección "Listo para Retirar" - Solo administradores */}
            {isAdmin &&
              orders.filter((x) => x.status === "SHOP_COMPLETED").length >
                0 && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200/50 p-6 lg:p-8">
                  <div className="flex items-center gap-3 mb-8">
                    <Icon
                      icon="lucide:box"
                      className="text-3xl text-amber-600"
                    />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                      Listo para Retirar
                    </h2>
                  </div>

                  {/* PICKUP Section */}
                  {orders.filter(
                    (x) =>
                      x.status === "SHOP_COMPLETED" &&
                      x.deliveryMethod === "PICKUP"
                  ).length > 0 && (
                    <div className="mb-12">
                      <div className="flex items-center gap-2 mb-6">
                        <Icon
                          icon="lucide:hand-pickup"
                          className="text-2xl text-blue-600"
                        />
                        <h3 className="text-xl font-bold text-gray-900">
                          Para Recoger
                        </h3>
                        <span className="ml-auto bg-blue-100 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full">
                          {
                            orders.filter(
                              (x) =>
                                x.status === "SHOP_COMPLETED" &&
                                x.deliveryMethod === "PICKUP"
                            ).length
                          }
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {orders
                          .filter(
                            (x) =>
                              x.status === "SHOP_COMPLETED" &&
                              x.deliveryMethod === "PICKUP"
                          )
                          ?.map((order) => (
                            <div
                              key={order.id}
                              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 hover:shadow-md transition-shadow"
                            >
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                  <p className="text-sm text-blue-600 font-semibold">
                                    ID Orden
                                  </p>
                                  <p className="text-lg font-bold text-gray-900">
                                    {order.referenceId || order.id.slice(0, 8)}
                                  </p>
                                </div>
                                <span className="text-2xl font-bold text-blue-600">
                                  {order.price}
                                </span>
                              </div>

                              <div className="bg-white rounded-lg p-3 mb-4 border border-blue-200">
                                <p className="text-xs text-gray-600 mb-1">
                                  Código de Retiro
                                </p>
                                <p className="font-mono font-bold text-lg text-gray-900">
                                  {order.confirmationCode || "N/A"}
                                </p>
                              </div>

                              <p className="text-xs text-gray-600 mb-4">
                                {new Date(order.createdAt).toLocaleDateString(
                                  "es-ES"
                                )}
                              </p>

                              <Button
                                size="sm"
                                color="success"
                                variant="shadow"
                                className="w-full font-semibold"
                                onPress={() =>
                                  handleCompletePickupOrder(order.id)
                                }
                                startContent={<Icon icon="lucide:check" />}
                              >
                                Marcar Completada
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* SHIPPING Section */}
                  {orders.filter(
                    (x) =>
                      x.status === "SHOP_COMPLETED" &&
                      x.deliveryMethod === "SHIPPING"
                  ).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-6">
                        <Icon
                          icon="lucide:truck"
                          className="text-2xl text-green-600"
                        />
                        <h3 className="text-xl font-bold text-gray-900">
                          Para Envío
                        </h3>
                        <span className="ml-auto bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded-full">
                          {
                            orders.filter(
                              (x) =>
                                x.status === "SHOP_COMPLETED" &&
                                x.deliveryMethod === "SHIPPING"
                            ).length
                          }
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {orders
                          .filter(
                            (x) =>
                              x.status === "SHOP_COMPLETED" &&
                              x.deliveryMethod === "SHIPPING"
                          )
                          ?.map((order) => (
                            <div
                              key={order.id}
                              className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200 hover:shadow-md transition-shadow"
                            >
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                  <p className="text-sm text-green-600 font-semibold">
                                    ID Orden
                                  </p>
                                  <p className="text-lg font-bold text-gray-900">
                                    {order.referenceId || order.id.slice(0, 8)}
                                  </p>
                                </div>
                                <span className="text-2xl font-bold text-green-600">
                                  {order.price}
                                </span>
                              </div>

                              <div className="bg-white rounded-lg p-3 mb-4 border border-green-200">
                                <p className="text-xs text-gray-600 mb-1">
                                  Código de Envío
                                </p>
                                <p className="font-mono font-bold text-lg text-gray-900">
                                  {order.confirmationCode || "N/A"}
                                </p>
                              </div>

                              <p className="text-xs text-gray-600 mb-4">
                                {new Date(order.createdAt).toLocaleDateString(
                                  "es-ES"
                                )}
                              </p>

                              <div className="bg-green-200 text-green-800 text-xs font-semibold px-3 py-2 rounded-lg text-center">
                                ⚡ Se actualizará automáticamente
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="lg"
        backdrop="blur"
        classNames={{
          base: "max-w-md lg:max-w-lg xl:max-w-xl",
          body: "py-6 lg:py-8",
          header: "pb-4 lg:pb-6",
          footer: "pt-4 lg:pt-6",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${modalAction === "deactivate" ? "bg-red-100" : "bg-green-100"}`}
                  >
                    <Icon
                      icon={
                        modalAction === "deactivate"
                          ? "lucide:power-off"
                          : "lucide:power"
                      }
                      className={`text-xl ${modalAction === "deactivate" ? "text-red-600" : "text-green-600"}`}
                    />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold">
                    {modalAction === "deactivate"
                      ? "Confirmar Desactivación"
                      : "Confirmar Activación"}
                  </h3>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <p className="text-gray-600 text-base lg:text-lg leading-relaxed">
                    {modalAction === "deactivate"
                      ? "¿Estás seguro de que quieres desactivar tu cuenta? Esta acción puede tener consecuencias importantes."
                      : "¿Estás seguro de que quieres activar tu cuenta?"}
                  </p>
                  {modalAction === "deactivate" && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Icon
                          icon="lucide:alert-triangle"
                          className="text-red-500 mt-0.5 flex-shrink-0"
                        />
                        <div className="text-sm text-red-700">
                          <p className="font-medium mb-1">Ten en cuenta:</p>
                          <ul className="list-disc list-inside space-y-1 text-xs lg:text-sm">
                            <li>No recibirás nuevos pedidos</li>
                            <li>Los pedidos actuales no se verán afectados</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter className="gap-3">
                <Button
                  startContent={<Icon icon="lucide:x" />}
                  color="default"
                  variant="light"
                  onPress={onClose}
                  size="lg"
                  className="font-medium px-6"
                >
                  Cancelar
                </Button>
                <Button
                  startContent={<Icon icon="lucide:check" />}
                  color={modalAction === "deactivate" ? "danger" : "primary"}
                  onPress={confirmAccountAction}
                  size="lg"
                  className="font-semibold px-6"
                  variant="shadow"
                >
                  {modalAction === "deactivate" ? "Desactivar" : "Activar"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

export default Home;
